import * as Yup from "yup";
import {
  isNotEmptyObj,
  isValidEmail,
  isValidPassword,
  isValidSlug,
  isValidDisplayCondition,
  isValidDomain,
  isValidUrl,
  isValidBrandSlug,
  isRequiredWithOther,
  isValidBonusCode,
  isRequiredString,
  isValidOptionalInterval,
  isValidJSON,
} from "./validationHelper";
import {
  checkKycConditionSets,
  validateChatSupportDisplayConditions,
} from "../../modules/Services/helpers";
import {
  CAPTCHA_TYPE,
  yupContextLevel1ExtendedType,
} from "../../modules/Services/constants";
import { checkConditionSets } from "../../modules/Services/helpers";
import { NOTIFICATION_CAMPAIGN_MAX_LIFETIME_DAYS } from "../../modules/NotificationCampaigns/constants";
import {
  contextLevel1ExtendedType,
  contextLevel3ExtendedType,
} from "../../modules/Services/components/restriction/constants";
import {
  isUniqueAmountSeries,
  isValidAmountSeriesInput,
} from "../../modules/Payments/helpers/helpfulFunctions";
import { dateUTCParser, isValidNumber } from "./helpfulFunctions";
import { parseExpirationIntreval } from "../../components/molecules/ExpirationIntervalField/helpers";
import { getTimeRangeError } from "../../components/molecules/RecurringSchedule/helpers";

export const LoginSchema = () =>
  Yup.object().shape({
    email: isValidEmail().required("Email field is required"),
    password: Yup.string().required("Password field is required"),
  });

export const ForgotPasswordSchema = () =>
  Yup.object().shape({
    email: isValidEmail().required("Email field is required"),
  });

export const ResetPasswordSchema = () =>
  Yup.object().shape({
    email: isValidEmail().required("Email field is required"),
    password: isValidPassword().required("Password field is required"),
    repeatPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("New password field is required"),
  });

export const AccountBaseFormSchema = (type) =>
  Yup.object().shape({
    name: Yup.string().required("Name field is required"),
    email: isValidEmail().required("Email field is required"),
    password:
      type === "edit"
        ? isValidPassword()
        : isValidPassword().required("Password field is required"),
  });

export const AccountBrandFormSchema = () =>
  Yup.object().shape({
    brands: Yup.array(),
  });

export const AccountRoleFormSchema = () =>
  Yup.object().shape({
    roles: Yup.array(),
  });

export const CustomerBaseFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().notRequired(),
    firstName: Yup.string().notRequired(),
    lastName: Yup.string().notRequired(),
    country: Yup.string().notRequired(),
    city: Yup.string().notRequired(),
    address: Yup.string().notRequired(),
    postalCode: Yup.string().notRequired(),
    email: isValidEmail().notRequired(),
  });

export const CustomerTagsFormSchema = () =>
  Yup.object().shape({
    vip: Yup.string().notRequired(),
  });

export const CustomerTfaFormSchema = () =>
  Yup.object().shape({
    tfa_type: Yup.string().notRequired(),
    tfa_enabled: Yup.boolean().notRequired(),
  });

export const CustomerRegTypeFormSchema = () =>
  Yup.object().shape({
    player_type: Yup.string().required(),
  });

export const RolePermissionFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Role name is required"),
    permissions: Yup.array().min(1).required("Permissions is required"),
  });

export const CreateBrandFormSchema = () =>
  Yup.object().shape({
    omega_id: Yup.number()
      .typeError("Brand id must be a number")
      .required("Brand id is required"),
    omega_instance_id: Yup.number()
      .typeError("Brand core host is required")
      .required("Brand core host is required"),
    name: Yup.string().required("Brand name is required"),
    token: Yup.string().required("Brand proxy token is required"),
    slug: isValidBrandSlug().notRequired(),
    settings: Yup.object().shape({
      color: Yup.string().required("Brand color is required"),
      cdn: Yup.string().required("CDN Host is required"),
      ws: Yup.string().notRequired(),
      sb_host: Yup.string().url("SB Host must be valid url").notRequired(),
      app_host: Yup.string().url("App host must be valid url").notRequired(),
      media_host: Yup.string()
        .url("Media host must be valid url")
        .notRequired(),
      bettech_brand_id: Yup.string().notRequired().nullable(),
      auth_secret: Yup.string().notRequired().nullable(),
      omega_checksum_secret: Yup.string().notRequired().nullable(),
      bettech_script: Yup.string()
        .max(250, "Script url must be at most 250 characters")
        .url("Must be valid url")
        .notRequired()
        .nullable(),
      use_cookie_access_token: Yup.boolean().notRequired(),
    }),
    countries: Yup.array().min(1).required("Countries is required"),
    currencies: Yup.array().min(1).required("Currencies is required"),
    languages: Yup.array().min(1).required("Languages is required"),
    country_currency_map: Yup.mixed()
      .test(
        "not empty",
        "Country to currency is required",
        (value) => !!Object.values(value).length
      )
      .required(),
    country_language_map: Yup.mixed()
      .test(
        "not empty",
        "Country to language is required",
        (value) => !!Object.values(value).length
      )
      .required(),
  });

export const CountryListSelectFormSchema = () =>
  Yup.object().shape({
    countries: Yup.array().min(1),
  });

export const LocalesAndCountriesFormSchema = () =>
  Yup.object().shape({
    currencies: Yup.array(),
    languages: Yup.array(),
    countries: Yup.array().min(1),
  });

export const BrandConfigurationSettingsFormSchema = () =>
  Yup.object().shape({
    omega_id: Yup.number()
      .typeError("Brand id must be a number")
      .required("Brand id is required"),
    name: Yup.string().required("Brand name is required"),
    token: Yup.string().required("Brand proxy token is required"),
    omega_instance_id: Yup.number().required("Brand core host is required"),
    slug: isValidBrandSlug().notRequired().nullable(),
    settings: Yup.object().shape({
      color: Yup.string().required("Brand color is required"),
      cdn: Yup.string().required("CDN Host is required"),
      ws: Yup.string().notRequired(),
      sb_host: Yup.string()
        .url("SB Host must be valid url")
        .notRequired()
        .nullable(),
      app_host: Yup.string()
        .url("App host must be valid url")
        .notRequired()
        .nullable(),
      media_host: Yup.string()
        .url("Media host must be valid url")
        .notRequired()
        .nullable(),
      bettech_brand_id: Yup.string().notRequired().nullable(),
      auth_secret: Yup.string().notRequired().nullable(),
      omega_checksum_secret: Yup.string().notRequired().nullable(),
      bettech_script: Yup.string()
        .max(250, "Script url must be at most 250 characters")
        .url("Must be valid url")
        .notRequired()
        .nullable(),
      use_cookie_access_token: Yup.boolean().notRequired(),
    }),
    countries: Yup.array().min(1),
  });

export const CreateGameFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    omega_id_desktop: Yup.string().required(
      "The omega id desktop field is required"
    ),
    slug: isValidSlug().required("Slug is required"),
    omega_id_mobile: Yup.string().required(
      "The omega id mobile field is required"
    ),
    provider: null,
    omega_launch_id_desktop: Yup.string().required(
      "The launch ID desktop field is required"
    ),
    omega_launch_id_mobile: Yup.string().required(
      "The launch ID mobile field is required"
    ),
    omega_start_params_desktop: Yup.string().required("Required"),
    omega_start_params_mobile: Yup.string().required("Required"),
    ratio: Yup.string().notRequired(),
    demo: Yup.boolean(),
    fullscreen: Yup.boolean(),
    title: Yup.object().required("The title field is required"),
    text: Yup.object().notRequired(),
    seo_title: Yup.object().notRequired(),
    seo_description: Yup.object().notRequired(),
    seo_keywords: Yup.object().notRequired(),
    seo_panel_title: Yup.object().notRequired(),
    seo_panel_text: Yup.object().notRequired(),
    canonical: Yup.object().notRequired(),
    alternate: Yup.object().notRequired(),
    ampdomain: Yup.object().notRequired(),
    desktop: Yup.string(),
    mobile: Yup.string(),
    background: Yup.string(),
    badge_new_until: Yup.string().notRequired().nullable(),
    badge_hot_until: Yup.string().notRequired().nullable(),
    show_badge_new: Yup.boolean(),
    show_badge_hot: Yup.boolean(),
  });

export const GameSettingsFormSchema = () =>
  Yup.object().shape({
    slug: isValidSlug().required("Slug is required"),
    provider: null,
    demo: Yup.boolean(),
    ratio: Yup.string().notRequired().nullable(),
    fullscreen: Yup.boolean(),
    title: Yup.object().required("The title field is required"),
    text: Yup.object().notRequired().nullable(),
    seo_title: Yup.object().notRequired().nullable(),
    seo_description: Yup.object().notRequired().nullable(),
    seo_keywords: Yup.object().notRequired().nullable(),
    seo_panel_title: Yup.object().notRequired().nullable(),
    seo_panel_text: Yup.object().notRequired().nullable(),
    canonical: Yup.object().notRequired().nullable(),
    alternate: Yup.object().notRequired().nullable(),
    ampdomain: Yup.object().notRequired().nullable(),
    options: Yup.object().notRequired().nullable(),
    badge_new_until: Yup.string().notRequired().nullable(),
    badge_hot_until: Yup.string().notRequired().nullable(),
    show_badge_new: Yup.boolean(),
    show_badge_hot: Yup.boolean(),
  });

export const GameSettingsGlobalConfigSchema = () =>
  GameSettingsFormSchema().shape({
    omega_id_desktop: Yup.string().required(
      "The omega id desktop field is required"
    ),
    omega_launch_id_desktop: Yup.string().required(
      "The launch ID desktop field is required"
    ),
    omega_start_params_desktop: Yup.string().required("Required"),
    omega_id_mobile: Yup.string().required(
      "The omega id mobile field is required"
    ),
    omega_launch_id_mobile: Yup.string().required(
      "The launch ID mobile field is required"
    ),
    omega_start_params_mobile: Yup.string().required("Required"),
  });

export const GameImagesFormSchema = () => {
  Yup.object().shape({
    desktop: Yup.string(),
    mobile: Yup.string(),
    background: Yup.string(),
  });
};

export const GameCategoriesFormSchema = () => {
  Yup.object().shape({
    id: Yup.number().notRequired(),
    categories: Yup.array().notRequired(),
  });
};

export const SettingsFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Required"),
    slug: isValidSlug().required("Slug is required"),
    omega_platforms: Yup.array().notRequired().nullable(),
    title: Yup.object().notRequired().nullable(),
    text: Yup.object().notRequired().nullable(),
    seo_title: Yup.object().notRequired().nullable(),
    seo_description: Yup.object().notRequired().nullable(),
    seo_keywords: Yup.object().notRequired().nullable(),
    seo_panel_title: Yup.object().notRequired().nullable(),
    seo_panel_text: Yup.object().notRequired().nullable(),
    canonical: Yup.object().notRequired().nullable(),
    alternate: Yup.object().notRequired().nullable(),
    ampdomain: Yup.object().notRequired().nullable(),
  });

export const CreateCategoryFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Name is required"),
    slug: isValidSlug().required("Slug is required"),
    title: isNotEmptyObj(),
    text: Yup.object().notRequired().nullable(),
    seo_title: Yup.object().notRequired().nullable(),
    seo_description: Yup.object().notRequired().nullable(),
    seo_keywords: Yup.object().notRequired().nullable(),
    seo_panel_title: Yup.object().notRequired().nullable(),
    seo_panel_text: Yup.object().notRequired().nullable(),
    canonical: Yup.object().notRequired().nullable(),
    alternate: Yup.object().notRequired().nullable(),
    ampdomain: Yup.object().notRequired().nullable(),
    image: Yup.object()
      .shape({
        normal: Yup.string(),
        active: Yup.string(),
      })
      .notRequired()
      .nullable(),
  });

export const ImagesFormSchema = () => {
  Yup.object().shape({
    normal: Yup.string(),
    active: Yup.string(),
  });
};

export const BrandsFormSchema = () =>
  Yup.object().shape({
    id: Yup.number().notRequired(),
    brands: Yup.array().notRequired(),
  });

export const RecurringScheduleSchema = () =>
  Yup.object({
    type: Yup.string().oneOf(["week", "month"]),
    days: Yup.array()
      .of(
        Yup.object({
          value: Yup.number().required(),
          from: Yup.string(),
          to: Yup.string(),
        }).test("valid-time-range", "", (day, ctx) => {
          const message = getTimeRangeError(day?.from ?? "", day?.to ?? "");

          return message ? ctx.createError({ message }) : true;
        })
      )
      .when("type", {
        is: (type: string) => !!type,
        then: (schema) =>
          schema
            .required("Select at least one day")
            .min(1, "Select at least one day"),
      }),
  })
    .notRequired()
    .nullable();

export const CreatePromotionFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean().notRequired().nullable(),
    name: Yup.string().required("Name is required"),
    omega_ids: Yup.array().nullable().notRequired(),
    omega_code: Yup.string()
      .notRequired()
      .nullable()
      .test(
        "required-if-lp-amount",
        "Bonus code is required while LP Amount entered",
        (value, ctx) =>
          !!ctx?.parent?.lp_amount && ctx?.parent.lp_amount >= 0
            ? !!value
            : true
      ),
    slug: isValidSlug().required("Slug is required"),
    lp_amount: Yup.number().notRequired().nullable(),
    tags: Yup.array().notRequired().nullable(),
    button_color: Yup.object().nullable(),
    button_text: Yup.object().nullable(),
    button_url: Yup.object().nullable(),
    url: Yup.object().notRequired().nullable(),
    title: Yup.object().notRequired().nullable(),
    text_short: Yup.object().notRequired().nullable(),
    text_full: Yup.object().notRequired().nullable(),
    display_condition: Yup.object(),
    seo_title: Yup.object().notRequired().nullable(),
    seo_description: Yup.object().notRequired().nullable(),
    seo_keywords: Yup.object().notRequired().nullable(),
    seo_panel_title: Yup.object().notRequired().nullable(),
    seo_panel_text: Yup.object().notRequired().nullable(),
    seo_robots_meta: Yup.object().notRequired().nullable(),
    canonical: Yup.object().notRequired().nullable(),
    alternate: Yup.object().notRequired().nullable(),
    ampdomain: Yup.object().notRequired().nullable(),
    show_from: Yup.string().notRequired().nullable(),
    show_to: Yup.string().notRequired().nullable(),
    show_schedule: RecurringScheduleSchema(),
  });

export const getPromotionSettingFormSchema = ({ countries, languages }) =>
  Yup.object().shape({
    name: Yup.string().required("Name is required"),
    omega_ids: Yup.array().nullable().notRequired(),
    omega_code: Yup.string()
      .notRequired()
      .nullable()
      .test(
        "required-if-lp-amount",
        "Bonus code is required while LP Amount entered",
        (value, ctx) =>
          !!ctx?.parent?.lp_amount && ctx?.parent.lp_amount >= 0
            ? !!value
            : true
      ),
    slug: isValidSlug().required("Slug is required"),
    lp_amount: Yup.number().notRequired().nullable(),
    tags: Yup.array().notRequired().nullable(),
    button_color: Yup.object().nullable(),
    button_text: Yup.object().nullable(),
    button_url: Yup.object().nullable(),
    url: Yup.object().notRequired().nullable(),
    title: Yup.object().notRequired().nullable(),
    text_short: Yup.object().notRequired().nullable(),
    text_full: Yup.object().notRequired().nullable(),
    seo_title: Yup.object().notRequired().nullable(),
    seo_description: Yup.object().notRequired().nullable(),
    seo_keywords: Yup.object().notRequired().nullable(),
    seo_panel_title: Yup.object().notRequired().nullable(),
    seo_panel_text: Yup.object().notRequired().nullable(),
    seo_robots_meta: Yup.object().notRequired().nullable(),
    canonical: Yup.object().notRequired().nullable(),
    alternate: Yup.object().notRequired().nullable(),
    ampdomain: Yup.object().notRequired().nullable(),
    display_condition: isValidDisplayCondition({
      countries,
      languages,
    }).required(),
  });

export const PromotionScheduleFormSchema = () =>
  Yup.object().shape(
    {
      show_from: Yup.string().when("show_to", {
        is: (value) => !!value,
        then: (schema) => schema.required("Start date is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      show_to: Yup.string().when("show_from", {
        is: (value) => !!value,
        then: (schema) => schema.required("End date is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      show_schedule: RecurringScheduleSchema(),
    },
    [["show_from", "show_to"]]
  );

export const CalendarFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().notRequired(),
    slug: Yup.string().required("Slug is required"),
    show_from: Yup.string().required("Start date is required"),
    show_to: Yup.string().required("End date is required"),
    template: Yup.string().required("Calendar type is required"),
    timezone: Yup.string().required("Timezone is required"),
  });

export const CalendarDayFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Calendar day is required"),
    date: Yup.string().required("Calendar day date is required").nullable(),
    title: Yup.object().notRequired().nullable(),
    text: Yup.object().notRequired().nullable(),
    description: Yup.object().notRequired().nullable(),
    button_text: Yup.object().required("Button text is required"),
    action_type: Yup.object().required("Action type is required"),
    action_value: Yup.object().required("Action value is required"),
    image_active: isNotEmptyObj("Active image is required"),
    image_upcoming: isNotEmptyObj("Upcoming image is required"),
    image_expired: isNotEmptyObj("Expired image is required"),
    image_popup: Yup.object().notRequired().nullable(),
  });

export const CalendarPeriodFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Name is required"),
    show_from: Yup.string().required("Start date is required"),
    show_to: Yup.string().required("End date is required"),
    title: Yup.object().notRequired().nullable(),
    description: Yup.object().notRequired().nullable(),
    image_popup: Yup.object().notRequired().nullable(),
    image_background: Yup.object().notRequired().nullable(),
  });

export const CreateBannerFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    name: Yup.string().required("Banner name is required"),
    image: isNotEmptyObj("Banner image is required"),
    title: Yup.object().nullable(),
    display_position: Yup.object().nullable(),
    display_condition: Yup.object(),
    text: Yup.object().nullable(),
    button_color: Yup.object().nullable(),
    button_text: Yup.object().nullable(),
    button_url: Yup.object().nullable(),
    url: Yup.object().nullable(),
    show_from: Yup.string(),
    show_to: Yup.string(),
    show_schedule: RecurringScheduleSchema(),
    js_banner: Yup.object(),
  });

export const getBannerSettingFormSchema = ({ countries, languages }) =>
  Yup.object().shape({
    name: Yup.string().required("Banner name is required"),
    image: isNotEmptyObj("Banner image is required"),
    title: Yup.object().nullable(),
    display_position: Yup.object().nullable(),
    display_condition: isValidDisplayCondition({
      countries,
      languages,
    }).required(),
    text: Yup.object().nullable(),
    button_color: Yup.object().nullable(),
    button_text: Yup.object().nullable(),
    button_url: Yup.object().nullable(),
    url: Yup.object().nullable(),
    js_banner: Yup.object(),
  });

export const BannerScheduleFormSchema = () =>
  Yup.object().shape({
    show_from: Yup.string().nullable(),
    show_to: Yup.string().nullable(),
    show_schedule: RecurringScheduleSchema(),
  });

export const CreateFaqFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    name: Yup.string().required("Name is required"),
    title: Yup.object().required("The title field is required"),
    description: Yup.object().notRequired().nullable(),
    content: Yup.object().notRequired().nullable(),
  });

export const FaqSettingFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Name is required"),
    title: Yup.object().required("The title field is required"),
    description: Yup.object().notRequired().nullable(),
    content: Yup.object().required(),
  });

export const FaqImagesFormSchema = () => {
  Yup.object().shape({
    image: Yup.string(),
  });
};

export const ProviderCreationFormSchema = () =>
  Yup.object().shape({
    slug: isValidSlug(),
    seo_title: Yup.object().notRequired().nullable(),
    seo_description: Yup.object().notRequired().nullable(),
    seo_keywords: Yup.object().notRequired().nullable(),
    seo_panel_title: Yup.object().notRequired().nullable(),
    seo_panel_text: Yup.object().notRequired().nullable(),
    canonical: Yup.object().notRequired().nullable(),
    alternate: Yup.object().notRequired().nullable(),
    ampdomain: Yup.object().notRequired().nullable(),
  });

export const BonusSettingFormSchema = () =>
  Yup.object().shape({
    id: Yup.number().notRequired().nullable(),
    name: Yup.string().required("Name is required"),
    type: Yup.string()
      .typeError("The trigger type should be selected")
      .required(),
    slug: isValidSlug().required("Slug is required"),
    plan_id: Yup.number()
      .optional()
      .nullable()
      .typeError("Bonus Plan Id should be a numeric"),
    bonus_code: Yup.mixed()
      .optional()
      .nullable()
      .test(
        "code-is-not-empty",
        "Required if Bonus Plan Id and Amount are not set.",
        (value, ctx) =>
          !!value ? true : ctx?.parent?.plan_id || ctx?.parent?.amount
      ),
    amount: Yup.number()
      .typeError("Amount should be a numeric")
      .optional()
      .nullable(),
    url: Yup.mixed().optional().nullable(),
    title: Yup.mixed().optional().nullable(),
    description: Yup.mixed().optional().nullable(),
    active: Yup.boolean(),
    show_from: Yup.string().required("Start date is required"),
    show_to: Yup.string().required("End date is required"),
    user_activations: Yup.number()
      .typeError("The activation field must be a number")
      .moreThan(0, "The activation field must be more then zero")
      .required("The activation field is required"),
    display_condition: Yup.object(),
    ftd_only: Yup.boolean(),
    reg_deposit_interval: Yup.string()
      .nullable()
      .when("type", {
        is: "deposit",
        then: (_schema) => isValidOptionalInterval(),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    delay_interval: isValidOptionalInterval(),
    distribution_condition: Yup.object()
      .when("type", {
        is: "deposit",
        then: (schema) =>
          schema.shape({
            method_ids: Yup.array()
              .min(1, "Method is required for deposit type")
              .required("Method is required for deposit type"),
          }),
        otherwise: (schema) => schema.notRequired(),
      })
      .when("type", {
        is: "party_id_list",
        then: (schema) =>
          schema.shape({
            party_id_list: Yup.array()
              .min(1, "Party IDs is required for current trigger type")
              .required("Party IDs is required for current trigger type"),
          }),
        otherwise: (schema) => schema.notRequired(),
      })
      .when("type", {
        is: "partner_code",
        then: (schema) =>
          schema.shape({
            partner_code: Yup.array()
              .min(1, "Partner code is required for current trigger type")
              .required("Partner code is required for current trigger type"),
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
    display_position: Yup.object().test(
      "is-not-empty",
      "Display position is required",
      (field) => field && Object.keys(field).length > 0
    ),
    image: Yup.object().optional().nullable(),
  });

export const CreatePageFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    name: Yup.string().required("Name is required"),
    display_condition: Yup.object(),
    display_position: Yup.object().required("Display position is required"),
    slug: isValidSlug().required("Slug is required"),
    title: Yup.object().required("The title is required"),
    text: Yup.object().notRequired().nullable(),
    seo_title: Yup.object().notRequired(),
    seo_description: Yup.object().notRequired(),
    seo_keywords: Yup.object().notRequired(),
    seo_robots_meta: Yup.object().notRequired(),
    canonical: Yup.object().notRequired(),
    alternate: Yup.object().notRequired(),
    ampdomain: Yup.object().notRequired(),
    image: Yup.object()
      .shape({
        normal: Yup.string(),
        active: Yup.string(),
      })
      .notRequired()
      .nullable(),
  });

export const getPageSettingFormSchema = ({ countries, languages }) =>
  Yup.object().shape({
    name: Yup.string().required("Name is required"),
    display_condition: isValidDisplayCondition({
      countries,
      languages,
    }).required(),
    display_position: Yup.object().required("Display position is required"),
    slug: isValidSlug().required("Slug is required"),
    url: Yup.string().required("Link is required").nullable(),
    title: Yup.object().required("The title is required"),
    text: Yup.object().notRequired().nullable(),
    seo_title: Yup.object().notRequired(),
    seo_description: Yup.object().notRequired(),
    seo_keywords: Yup.object().notRequired(),
    seo_robots_meta: Yup.object().notRequired(),
    canonical: Yup.object().notRequired(),
    alternate: Yup.object().notRequired(),
    ampdomain: Yup.object().notRequired(),
    image: Yup.object()
      .shape({
        normal: Yup.string(),
        active: Yup.string(),
      })
      .notRequired()
      .nullable(),
  });

const termsBaseFormFields = {
  name: Yup.string().required("Name is required"),
  slug: isValidSlug().required("Slug is required"),
  secret: Yup.string().notRequired().nullable(),
  title: Yup.object().required("Title is required"),
  text: Yup.object().notRequired().nullable(),
  attachment: Yup.mixed().notRequired().nullable(),
};

export const CreateTermsFormSchema = () =>
  Yup.object().shape({
    ...termsBaseFormFields,
    active: Yup.boolean(),
  });

export const TermsSettingFormSchema = () =>
  Yup.object().shape({
    ...termsBaseFormFields,
  });

export const CreateNewsFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    name: Yup.string().required("Name is required"),
    slug: isValidSlug().required("Slug is required"),
    tags: Yup.array().required().min(1, "Tags field must have at least 1 item"),
    title: Yup.object().required("The title is required"),
    text_full: Yup.object().notRequired().nullable(),
    text_short: Yup.object().notRequired().nullable(),
    image: Yup.object().required("Image is required"),
    show_from: Yup.string().required("Start date is required"),
    show_to: Yup.string().required("End date is required"),
    seo_title: Yup.object().notRequired(),
    seo_description: Yup.object().notRequired(),
    seo_keywords: Yup.object().notRequired(),
    seo_robots_meta: Yup.object().notRequired(),
  });

export const NewsSettingFormSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Name is required"),
    slug: isValidSlug().required("Slug is required"),
    tags: Yup.array().required().min(1, "Tags field must have at least 1 item"),
    title: Yup.object().required("The title field is required"),
    text_full: Yup.object().notRequired().nullable(),
    text_short: Yup.object().notRequired().nullable(),
    image: Yup.object().required("Image is required"),
    seo_title: Yup.object().notRequired(),
    seo_description: Yup.object().notRequired(),
    seo_keywords: Yup.object().notRequired(),
    seo_robots_meta: Yup.object().notRequired(),
  });

export const NewsScheduleFormSchema = () =>
  Yup.object().shape({
    show_from: Yup.string().required("Start date is required"),
    show_to: Yup.string().required("End date is required"),
  });

export const GamesFormSchema = () => {
  Yup.object().shape({
    id: Yup.number().notRequired(),
    games: Yup.array().notRequired(),
    providers: Yup.array().notRequired(),
    available: Yup.array().notRequired(),
    active: Yup.array().notRequired(),
  });
};

export const EntityFormSchema = () => {
  Yup.object().shape({
    id: Yup.number().notRequired(),
    providers: Yup.array().notRequired(),
    categories: Yup.array().notRequired(),
    available: Yup.array().notRequired(),
    active: Yup.array().notRequired(),
  });
};

export const BrandPaymentFormSchema = () => {
  Yup.object().shape({
    id: Yup.number().notRequired(),
    payments: Yup.array().notRequired(),
    available: Yup.array().notRequired(),
    active: Yup.array().notRequired(),
  });
};

export const CreateImageFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    name: Yup.string().required("Name is required"),
    url: Yup.string().required("Url is required"),
    display_position: Yup.object().required("Display position is required"),
    display_condition: Yup.object(),
    title: Yup.object().required("The title is required"),
    image: Yup.string().required("Image is required"),
  });

export const getImagesSettingFormSchema = ({ countries, languages }) =>
  Yup.object().shape({
    name: Yup.string().required("Name is required"),
    url: Yup.string().required("Url is required"),
    display_position: Yup.object().required("Display position is required"),
    title: Yup.object().required("The title is required"),
    display_condition: isValidDisplayCondition({
      countries,
      languages,
    }).required(),
  });

export const ImagesImageFormSchema = () =>
  Yup.object().shape({
    image: Yup.string().required("Image is required"),
  });

export const UploadModalFormSchema = (validationParams) =>
  Yup.object().shape({
    file: Yup.mixed()
      .test("fileSize", "File size is too large", (value) => {
        const file = value as File | undefined;

        return file && validationParams.fileSizeLimit
          ? file?.size <= validationParams.fileSizeLimit
          : true;
      })
      .test("type", "Unsupported file type", (value) => {
        const file = value as File | undefined;

        return file && validationParams.types && validationParams.types.length
          ? validationParams.types.includes(file?.type)
          : true;
      }),
  });

export const SeoFormSchema = () =>
  Yup.object().shape({
    hreflang: isNotEmptyObj("Hreflang is required"),
    domain_number: Yup.object().notRequired().nullable(),
    canonical_locale: isNotEmptyObj("Canonical is required"),
    alternate_locale: Yup.object().notRequired().nullable(),
    ampdomain_locale: Yup.object().notRequired().nullable(),
    robots_meta_locale: Yup.object().notRequired().nullable(),
  });

export const InfoModalFormSchema = () =>
  Yup.object().shape({
    value: Yup.string().required("Value cannot be empty"),
  });

export const GatewayCreateFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    omega_id: Yup.string().required("ID is required"),
    name: Yup.string().required("Name is required"),
    strategy: Yup.object().shape({
      desktop: Yup.string().required("Desktop strategy is required"),
      mobile: Yup.string().required("Mobile strategy is required"),
    }),
    input: isValidJSON(),
    input_v2: isValidJSON(),
  });

export const GatewaySettingsFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    omega_id: Yup.string().required("ID is required"),
    name: Yup.string().required("Name is required"),
    strategy: Yup.object().shape({
      desktop: Yup.string().required("Desktop strategy is required"),
      mobile: Yup.string().required("Mobile strategy is required"),
    }),
    input: isValidJSON(),
    input_v2: isValidJSON(),
    transaction_check: Yup.boolean().notRequired(),
  });

export const GatewaySettingsBrandSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    strategy: Yup.object()
      .shape({
        desktop: Yup.string().notRequired(),
        mobile: Yup.string().notRequired(),
      })
      .notRequired()
      .nullable(),
    transaction_check: Yup.boolean().notRequired(),
  });

export const GatewayPaymentInputSchema = () =>
  Yup.object().shape({
    value: isValidJSON(),
  });

export const DomainCommentTextAreaSchema = () =>
  Yup.object().shape({
    value: Yup.string().notRequired(),
  });

export const TranslationKeyTextAreaSchema = (checkKeyExist, errMsg) =>
  Yup.object().shape({
    value: Yup.string().test(
      "is-uniq-key",
      errMsg,
      (value) => !checkKeyExist(value)
    ),
  });

const isValidAmountSeriesMap = () =>
  Yup.lazy((obj) =>
    Yup.object()
      .shape(
        Object.keys(obj || {}).reduce(
          (acc, currency) => ({
            ...acc,
            [currency]: Yup.string()
              .test(
                "amount-series-format",
                "Amount series has invalid values",
                (value) => isValidAmountSeriesInput(value)
              )
              .test(
                "amount-series-unique",
                "Amount series must not contain duplicate values",
                (value) => isUniqueAmountSeries(value)
              )
              .nullable(),
          }),
          {}
        )
      )
      .notRequired()
      .nullable()
  );

export const PaymentFormSchema = () =>
  Yup.object().shape({
    title: Yup.object().notRequired().nullable(),
    name: Yup.string().required("Name is required"),
    slug: isValidSlug().required("Slug is required"),
    info_text: Yup.object().notRequired().nullable(),
    amount_default: Yup.object().notRequired().nullable(),
    amount_series: isValidAmountSeriesMap(),
    amount_multiplicity: Yup.object().notRequired().nullable(),
    image: Yup.object().required("Image is required"),
  });

export const PaymentCategoryFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    id: Yup.string().notRequired().nullable(),
    slug: isValidSlug().required("Slug is required"),
    name: Yup.string().notRequired(),
    display_position: Yup.object().notRequired().nullable(),
    title: Yup.object().notRequired().nullable(),
    text: Yup.object().notRequired().nullable(),
    seo_title: Yup.object().notRequired().nullable(),
    seo_description: Yup.object().notRequired().nullable(),
    seo_keywords: Yup.object().notRequired().nullable(),
    methods: Yup.array().notRequired(),
    image: Yup.object()
      .shape({
        normal: Yup.string(),
        active: Yup.string(),
      })
      .notRequired()
      .nullable(),
  });

export const PaymentCategoryMethodsSchema = () =>
  Yup.object().shape({
    methods: Yup.object().required("Methods are required"),
  });

export const PaymentFormBrandSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Name is required"),
    slug: isValidSlug().required("Slug is required"),
    amount_series: isValidAmountSeriesMap(),
  });

export const OrderMethodFormSchema = () =>
  Yup.object().shape({
    methods: Yup.array().required("Methods are required"),
  });

export const GatewayBarabutFormSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
  reseller: Yup.string().required("Reseller is required"),
  sender: Yup.string().required("Sender is required"),
  uri: Yup.string().required("URI is required"),
});

export const GatewayPlivoFormSchema = Yup.object().shape({
  auth_id: Yup.string().required("Auth ID is required"),
  auth_token: Yup.string().required("Auth token is required"),
  email: Yup.string().required("Email is required"),
  phone: Yup.string().required("Phone is required"),
});

export const GatewaySmstoFormSchema = Yup.object().shape({
  api_key: Yup.string().required("API key is required"),
  client_id: Yup.string().required("Client ID is required"),
});

export const GatewayTwilioFormSchema = Yup.object().shape({
  sid: Yup.string().required("SID is required"),
  token: Yup.string().required("Token is required"),
  from: Yup.string().required("From field is required"),
});

export const GatewayTelegramFormSchema = Yup.object().shape({
  api_token: Yup.string().required("Api Key is required"),
});

export const GatewayMailchimpFormSchema = Yup.object().shape({
  api_key: Yup.string().required("Api key is required"),
  from_email: Yup.string().required("Email is required"),
});

export const GatewaySendgridFormSchema = Yup.object().shape({
  api_key: Yup.string().required("Api key is required"),
  from: isValidEmail().required("Email is required"),
  from_name: Yup.string(),
});

export const GatewayWavixFormSchema = Yup.object().shape({
  api_key: Yup.string().required("Api key is required"),
  sender_id: Yup.string().required("Sender id is required"),
});

export const GatewayLaafficFormSchema = Yup.object().shape({
  app_id: Yup.string().required("App ID is required"),
  api_key: Yup.string().required("API key is required"),
  api_secret: Yup.string().required("API secret is required"),
  sender_id: Yup.string(),
});

export const TrueplayFormSchema = Yup.object().shape({
  url: isValidDomain().required("URL is required"),
  api_key: Yup.string().required("API key is required"),
  callback_secret: Yup.string().notRequired().nullable(),
  integration_url: isValidUrl().notRequired().nullable(),
  integration_api_key: Yup.string().notRequired().nullable(),
});

export const ServicePasskeysAuthFormSchema = Yup.object().shape({
  url: isValidDomain().required("Url is required"),
  tenant: isValidDomain().required("Url is required"),
  expiration_time: Yup.number().required("Expiration time is required"),
});

export const ServicePasskeysFormSchema = Yup.object().shape({
  host: isValidDomain().required("Host is required"),
  token: Yup.string().required("Token is required"),
});

export const ServiceZingAIFormSchema = Yup.object().shape({
  url: Yup.string().required("Url is required"),
  zing_brand_id: Yup.string().required("Zing brand id is required"),
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

export const ServiceBtagMappingFormSchema = Yup.object().shape({
  map: Yup.array()
    .of(
      Yup.object().shape({
        passcode: Yup.string().test(
          "passcode-required-if-btag",
          "Passcode is required",
          function (value) {
            const { btag } = this.parent;
            return !btag || !!value;
          }
        ),
        btag: Yup.string().test(
          "btag-required-if-passcode",
          "Btag is required",
          function (value) {
            const { passcode } = this.parent;
            return !passcode || !!value;
          }
        ),
      })
    )
    .test("no-empty-objects", "", function (entries) {
      if (!Array.isArray(entries)) return true;
      const hasFullyEmptyRow = entries.some(
        (item) => !item.passcode && !item.btag
      );
      return !hasFullyEmptyRow;
    })
    .test(
      "unique-values",
      "Duplicate passcode or btag found",
      function (entries) {
        if (!Array.isArray(entries)) return true;
        const passcodeSet = new Set();

        for (let i = 0; i < entries.length; i++) {
          const { passcode } = entries[i];
          if (passcode) {
            if (passcodeSet.has(passcode)) {
              return this.createError({
                path: `settings.map[${i}].passcode`,
                message: "Passcode is already in use",
              });
            }
            passcodeSet.add(passcode);
          }
        }
        return true;
      }
    ),
});

export const ServiceAingAIFormSchema = Yup.object().shape({
  url: Yup.string().required("Url is required"),
  brand_uuid: Yup.string().required("Wizhdom brand id is required"),
  api_key: Yup.string().required("API key is required"),
});

const chatSupportProviderFormSchema = (): Yup.AnyObjectSchema =>
  Yup.object().shape({
    active: Yup.boolean(),
    api_key: Yup.string()
      .nullable()
      .when("active", {
        is: true,
        then: (schema) => schema.required("API key is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    button_key: Yup.mixed().notRequired().nullable(),
    display_condition: Yup.object().notRequired().nullable(),
  });

export const ServiceChatSupportFormSchema = Yup.object()
  .shape({
    livechat: chatSupportProviderFormSchema(),
    zendesk: chatSupportProviderFormSchema(),
    liveagent: chatSupportProviderFormSchema(),
    tawk: chatSupportProviderFormSchema(),
    intercom: chatSupportProviderFormSchema(),
    respondio: chatSupportProviderFormSchema(),
    wfm: chatSupportProviderFormSchema(),
  })
  .test(
    "no-conditions-for-active-providers",
    null,
    validateChatSupportDisplayConditions
  );

export const ServiceZeroBounceFormSchema = Yup.object().shape({
  api_key: Yup.string().required("API key is required"),
  validation_per_minute: Yup.number().required("Validation value is required"),
  cache_time: Yup.number().required("Cache time is required"),
});

export const ServiceAppsflyerFormSchema = Yup.object().shape({
  app_id: Yup.string()
    .required("App ID is required")
    .matches(
      /^[a-z0-9._]+$/,
      "App ID can only contain lowercase letters, numbers, dots, and underscores"
    ),
  api_key: Yup.string().required("API key is required"),
  ftd_event_name: Yup.string()
    .required("FTD event name is required")
    .matches(
      /^[a-z0-9_]+$/,
      "FTD event name can only contain lowercase letters, numbers, and underscores"
    ),
  deposit_event_name: Yup.string()
    .required("Deposit event name is required")
    .matches(
      /^[a-z0-9_]+$/,
      "Deposit event name can only contain lowercase letters, numbers, and underscores"
    ),
});

export const ServiceHeapFormSchema = Yup.object().shape({
  app_id: Yup.string().required("App ID is required"),
  url: Yup.string().required("URL is required").url("URL must be a valid URL"),
  deposit_event_name: Yup.string()
    .notRequired()
    .matches(/^[a-zA-Z0-9 _-]+$/, {
      message:
        "Deposit event name can only contain letters, numbers, spaces, underscores, and dashes",
      excludeEmptyString: true,
    }),
  registration_event_name: Yup.string()
    .notRequired()
    .matches(/^[a-zA-Z0-9 _-]+$/, {
      message:
        "Registration event name can only contain letters, numbers, spaces, underscores, and dashes",
      excludeEmptyString: true,
    }),
  login_event_name: Yup.string()
    .notRequired()
    .matches(/^[a-zA-Z0-9 _-]+$/, {
      message:
        "Login event name can only contain letters, numbers, spaces, underscores, and dashes",
      excludeEmptyString: true,
    }),
  failed_auth_event_name: Yup.string()
    .notRequired()
    .matches(/^[a-zA-Z0-9 _-]+$/, {
      message:
        "Auth failed event name can only contain letters, numbers, spaces, underscores, and dashes",
      excludeEmptyString: true,
    }),
});

export const ServicePostviewFormSchema = Yup.object().shape({
  app_id: Yup.string().required("ID is required"),
  api_key: Yup.string().required("Key is required"),
});

export const ServiceAuthSettingsFormSchema = Yup.object().shape({
  one_click: Yup.object().shape({
    signup_default: Yup.boolean(),
    signup: Yup.boolean(),
    email_domain: Yup.string().when(["signup"], {
      is: (signup) => !!signup,
      then: (schema) => schema.required("Email domain is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
  social_network: Yup.object().shape({
    google: Yup.object().shape({
      enabled: Yup.boolean(),
      client_id: Yup.string().when(["enabled"], {
        is: (enabled) => !!enabled,
        then: (schema) => schema.required("Client id is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  }),
  anonymous: Yup.object().shape({
    signup: Yup.boolean(),
  }),
  phone: Yup.object().shape({
    signup: Yup.boolean(),
    signin: Yup.boolean(),
  }),
  moje_id: Yup.object().shape({
    signup: Yup.boolean(),
  }),
  m_obywatel: Yup.object().shape({
    signup: Yup.boolean(),
  }),
  web3: Yup.object().shape({
    enabled: Yup.boolean(),
  }),
});

const eventFieldsSchema = Yup.object({
  enabled: Yup.boolean().required("Enabled status is required"),
  count: Yup.number().required("Events count is required"),
  interval: Yup.number().required("Interval is required"),
});

export const GatewayIovationFormSchema = Yup.object().shape({
  deposit: eventFieldsSchema,
  withdraw: eventFieldsSchema,
  login: eventFieldsSchema,
});

export const GatewayCoveryFormSchema = Yup.object().shape({
  login: eventFieldsSchema,
  signup: Yup.object({
    enabled: Yup.boolean().required("Enabled status is required"),
  }),
  deposit: eventFieldsSchema,
  withdraw: eventFieldsSchema,
  token: Yup.string().required(),
  secret: Yup.string().required(),
  host: Yup.string().required(),
});

const KycServiceSchema = Yup.lazy((obj) =>
  Yup.object(
    Object.keys(obj || {}).reduce((acc, key) => {
      acc[key] = Yup.object().shape({
        option: Yup.string()
          .oneOf(["internal", "kyc_aid"])
          .required("Option is required"),

        kyc_aid: Yup.object()
          .shape({
            token: Yup.string().required("Token is required"),
            form: Yup.object()
              .shape({
                id: Yup.string().required("Form ID is required"),
                name: Yup.string().required("Form name is required"),
              })
              .required("Form is required"),
            conditions: Yup.array().of(
              Yup.object()
                .shape({
                  antifraud_status: Yup.array()
                    .of(Yup.string())
                    .test(
                      "empty-status-check",
                      "Please select at least one status to configure condition.",
                      (value, ctx) => {
                        const kyc_status = ctx?.parent?.kyc_status;
                        return !!kyc_status?.length || !!value?.length;
                      }
                    )
                    .test(
                      "duplicate-status-check",
                      `Each combination of conditions must be unique for ${key} locale`,
                      (value, ctx) => {
                        const { from, options } = ctx as Yup.TestContext &
                          yupContextLevel1ExtendedType;
                        const values = from?.[1]?.value?.conditions;
                        const currentIndex = options?.index;
                        return checkKycConditionSets(values, currentIndex);
                      }
                    ),
                  kyc_status: Yup.array().of(Yup.string()),
                  form: Yup.object()
                    .shape({
                      id: Yup.string().required("Form ID is required"),
                      name: Yup.string().required("Form name is required"),
                    })
                    .required("Form is required"),
                })
                .notRequired()
            ),
          })
          .when("option", {
            is: "kyc_aid",
            then: (schema) => schema.required("KYC AID is required"),
            otherwise: (schema) => schema.notRequired().strip(),
          }),

        internal: Yup.object()
          .shape({
            identifier: Yup.object()
              .shape({
                enabled: Yup.boolean().required("Enabled is required"),
                required: Yup.boolean().required("Required is required"),
                documents: Yup.array()
                  .of(Yup.string().oneOf(["id", "passport", "license"]))
                  .required("Documents are required"),
              })
              .required("Identifier is required"),
            verifier: Yup.object()
              .shape({
                enabled: Yup.boolean().required("Enabled is required"),
                required: Yup.boolean().required("Required is required"),
                documents: Yup.array()
                  .of(Yup.string().oneOf(["address statement", "bank bill"]))
                  .required("Documents are required"),
              })
              .required("Verifier is required"),
            card: Yup.object()
              .shape({
                enabled: Yup.boolean().required("Enabled is required"),
                required: Yup.boolean().required("Required is required"),
                sides: Yup.array()
                  .of(Yup.string().oneOf(["front", "back"]))
                  .required("Sides are required"),
              })
              .required("Card is required"),
          })
          .when("option", {
            is: "internal",
            then: (schema) => schema.required("Internal is required"),
            otherwise: (schema) => schema.notRequired().strip(),
          }),
      });
      return acc;
    }, {})
  )
);

export const ServiceGameProxyFormSchema = Yup.object().shape({
  settings: isValidJSON().required("Proxy is required."),
});

export const ScammerDynamicFormSchema = Yup.object().shape({
  settings: isValidJSON().required("Dynamic scammer setting is required."),
});

export const ServiceShieldFormSchema = Yup.object().shape({
  settings: isValidJSON().required("Shield setting is required."),
});

export const ServiceBlinkOneClickFormSchema = Yup.object().shape({
  pos_id: Yup.string().required("Pos ID is required"),
  secret_id: Yup.string().required("Secret ID is required"),
  domain: isValidDomain().required("Domain is required"),
});

export const ServiceRestrictionFormSchema = Yup.lazy((obj) =>
  Yup.object().shape(
    Object.keys(obj).reduce(
      (acc, lang) => ({
        ...acc,
        [lang]: Yup.array().of(
          Yup.object().shape({
            active: Yup.boolean(),
            conditions: Yup.object().shape({
              kyc_status: Yup.object().shape({
                value: Yup.array()
                  .of(Yup.string())
                  .test(
                    "empty-status-check",
                    "Please specify at least one status, Tag, Btag or Bonus Code to configure restriction.",
                    (value, ctx) => {
                      const { from } = ctx as Yup.TestContext &
                        contextLevel1ExtendedType;
                      const antifraud_status =
                        from?.[1]?.value?.antifraud_status?.value;
                      const btag = from?.[1]?.value?.btag?.value;
                      const tag = from?.[1]?.value?.tag?.value;
                      const bonusCode = from?.[1]?.value?.bonus_code?.value;
                      return (
                        !!antifraud_status?.length ||
                        !!btag?.length ||
                        !!tag?.length ||
                        !!bonusCode?.length ||
                        value?.length !== 0
                      );
                    }
                  )
                  .test(
                    "duplicate-status-check",
                    `Please make sure each condition has unique set of statuses, Tags, Btags, and Bonus Codes.`,
                    (_value, ctx) => {
                      const { from, options } = ctx as Yup.TestContext &
                        contextLevel3ExtendedType;
                      const values = from?.[3]?.value?.[lang];
                      const currentIndex = options?.index;
                      return checkConditionSets(values, currentIndex);
                    }
                  ),
              }),
              antifraud_status: Yup.object().shape({
                value: Yup.array().of(Yup.string().notRequired()),
              }),
              btag: Yup.object().shape({
                value: Yup.array().of(Yup.string().notRequired()),
              }),
              tag: Yup.object().shape({
                value: Yup.array().of(Yup.string().notRequired()),
              }),
              bonus_code: Yup.object().shape({
                value: Yup.array().of(Yup.string().notRequired()),
              }),
            }),
            restrictions: Yup.object().test(
              "empty-condition-check",
              "At least one condition should be enabled",
              (obj) => Object.keys(obj).length > 0
            ),
          })
        ),
      }),
      {}
    )
  )
);

export const ServiceScammerCookieFormSchema = Yup.object().shape({
  enable_verification: Yup.boolean(),
  cookie_name: Yup.string().required("Cookie name is required"),
});

const licenceProviderSchema = (name) =>
  Yup.object().shape({
    active: Yup.boolean(),
    api_key: Yup.string()
      .max(500, "API Key must be at most 500 characters")
      .test("required", "API Key is required", (value, ctx) =>
        ctx.parent?.active ||
        !!ctx.parent?.token ||
        ctx.parent?.domain_types?.length > 0
          ? !!value
          : true
      )
      .nullable(),
    token: Yup.string()
      .max(500, "Token must be at most 500 characters")
      .nullable(),
    domain_types: Yup.array()
      .of(Yup.string())
      .test("required", "Domain type is required", (value, ctx) =>
        ctx.parent?.active || !!ctx.parent?.api_key || !!ctx.parent?.token
          ? value?.length > 0
          : true
      )
      .test(
        "already-used",
        "Domain type cannot have more than one selected license.",
        (value, ctx) => {
          const { from } = ctx as Yup.TestContext &
            yupContextLevel1ExtendedType;

          if (!value) {
            return true;
          }

          const providersObj = from?.[1]?.value;
          const providersArr = Object.keys(providersObj);
          const currentIndex = providersArr.indexOf(name);
          let res = true;

          for (let i = 0; i <= providersArr.length - 1; i++) {
            if (
              !providersObj[providersArr[i]]?.domain_types ||
              providersObj[providersArr[i]]?.domain_types?.length === 0 ||
              i === currentIndex
            ) {
              continue;
            }

            res = !value.some((el) =>
              providersObj[providersArr[i]]?.domain_types.includes(el)
            );

            if (!res) break;
          }

          return res;
        }
      ),
  });

export const ServiceLicenceFormSchema = Yup.object().shape({
  anjouan: licenceProviderSchema("anjouan"),
  curacao: licenceProviderSchema("curacao"),
  em_group: licenceProviderSchema("em_group"),
});

export const ServiceEmailageFormSchema = Yup.object().shape({
  client_secret: Yup.string().required("Client secret is required"),
  client_id: Yup.string().required("Client ID is required"),
  base_url: isValidUrl().required("URL is required"),
  validation_per_minute: Yup.number()
    .required("Validation value is required")
    .nullable(),
  cache_time: Yup.number().required("Cache time is required").nullable(),
  score: Yup.number().required("EA score threshold is required").nullable(),
});

export const ServiceSharedLuckFormSchema = Yup.object().shape({
  host: isValidUrl().required("Core URL is required"),
  key: Yup.string().required("API key is required"),
  games: Yup.array()
    .of(Yup.string())
    .min(1, "At least one game is required")
    .required("Games list is required"),
});

const settingsSchema = Yup.object({
  auth_provider_x509_cert_url: isRequiredString("auth_provider_x509_cert_url"),
  auth_uri: isRequiredString("auth_uri"),
  client_email: isRequiredString("client_email"),
  client_id: isRequiredString("client_id"),
  client_x509_cert_url: isRequiredString("client_x509_cert_url"),
  private_key: isRequiredString("private_key"),
  private_key_id: isRequiredString("private_key_id"),
  project_id: isRequiredString("project_id"),
  token_uri: isRequiredString("token_uri"),
  universe_domain: isRequiredString("universe_domain"),
}).strict();

export const ServiceFirebaseFormSchema = Yup.object().shape({
  settings: isValidJSON()
    .required("Firebase setting is required.")
    .test("fields-check", "", async (value, context) => {
      if (typeof value !== "string") {
        return false;
      }

      try {
        const parsedData = JSON.parse(value);
        await settingsSchema.validate(parsedData, { abortEarly: false });

        return true;
      } catch (error: any) {
        if (error.name === "ValidationError") {
          return context.createError({ message: error.errors.join(", ") });
        }

        return false;
      }
    }),
});

export const ServiceCapcthaFormSchema = Yup.object().shape({
  challenge_ttl: Yup.number().notRequired().nullable(),
  project_id: Yup.string()
    .nullable()
    .when("type", {
      is: CAPTCHA_TYPE.ENTERPRISE,
      then: (schema) => schema.required("Project ID is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  key: Yup.object()
    .nullable()
    .when("type", {
      is: CAPTCHA_TYPE.ENTERPRISE,
      then: (schema) =>
        schema.shape({
          api: Yup.string().nullable().required("API key is required"),
          site: Yup.string().nullable().required("Site key is required"),
        }),
    })
    .when("type", {
      is: (val) => [CAPTCHA_TYPE.V2, CAPTCHA_TYPE.V3].includes(val),
      then: (schema) =>
        schema.shape({
          site: Yup.string().nullable().required("Site key is required"),
          secret: Yup.string().nullable().required("Secret key is required"),
        }),
    }),
  prosopo: Yup.object()
    .nullable()
    .when("type", {
      is: CAPTCHA_TYPE.PROSOPO,
      then: (schema) =>
        schema.shape({
          key: Yup.object()
            .nullable()
            .shape({
              site: Yup.string().nullable().required("Site key is required"),
              secret: Yup.string()
                .nullable()
                .required("Secret key is required"),
            }),
        }),
    }),
});

export const IntegrationSettingsFieldsFormSchema = Yup.object().when(
  "alias",
  ([alias]) => {
    switch (alias) {
      case "plivo":
        return GatewayPlivoFormSchema;
      case "barabut":
        return GatewayBarabutFormSchema;
      case "mailchimp":
        return GatewayMailchimpFormSchema;
      case "sendGrid":
        return GatewaySendgridFormSchema;
      case "smsto":
        return GatewaySmstoFormSchema;
      case "twilio":
        return GatewayTwilioFormSchema;
      case "telegram":
        return GatewayTelegramFormSchema;
      case "iovation":
        return GatewayIovationFormSchema;
      case "covery":
        return GatewayCoveryFormSchema;
      case "aing":
        return ServiceAingAIFormSchema;
      case "zing":
        return ServiceZingAIFormSchema;
      case "passkeysAuth":
        return ServicePasskeysAuthFormSchema;
      case "passkeys":
        return ServicePasskeysFormSchema;
      case "chatSupport":
        return ServiceChatSupportFormSchema;
      case "btag":
        return ServiceBtagMappingFormSchema;
      case "kyc":
        return KycServiceSchema;
      case "zeroBounce":
        return ServiceZeroBounceFormSchema;
      case "authSettings":
        return ServiceAuthSettingsFormSchema;
      case "wavix":
        return GatewayWavixFormSchema;
      case "laaffic":
        return GatewayLaafficFormSchema;
      case "trueplay":
        return TrueplayFormSchema;
      case "blikOneClick":
        return ServiceBlinkOneClickFormSchema;
      case "restriction":
        return ServiceRestrictionFormSchema;
      case "scammerCookie":
        return ServiceScammerCookieFormSchema;
      case "licence":
        return ServiceLicenceFormSchema;
      case "appsflyer":
        return ServiceAppsflyerFormSchema;
      case "heap":
        return ServiceHeapFormSchema;
      case "adwise":
        return ServicePostviewFormSchema;
      case "emailage":
        return ServiceEmailageFormSchema;
      case "captcha":
        return ServiceCapcthaFormSchema;
      case "sharedluck":
        return ServiceSharedLuckFormSchema;
    }
  }
);

export const CreateIntegrationFormSchema = Yup.object().shape({
  alias: Yup.string().required("Alias is required"),
  name: Yup.string().required("Name is required"),
  settings: IntegrationSettingsFieldsFormSchema,
});

export const IntegrationSettingsFormSchema = Yup.object().shape({
  alias: Yup.string().required("Alias is required"),
  name: Yup.string().required("Name is required"),
  settings: IntegrationSettingsFieldsFormSchema,
});

const quizBaseFormFields = {
  name: Yup.string().required("Name is required"),
  slug: isValidSlug().required("Slug is required"),
  title: Yup.mixed()
    .test(
      "not empty",
      "The title field is required",
      (value) => !!Object.values(value).length
    )
    .required(),
  description: Yup.object().notRequired().nullable(),
  image: Yup.object().notRequired().nullable(),
  show_from: Yup.string().required("Show from date is required"),
  show_to: Yup.string().required("Show to date is required"),
  active: Yup.boolean(),
  questions: Yup.array().notRequired().nullable(),
};

export const CreateQuizFormSchema = () =>
  Yup.object().shape({ ...quizBaseFormFields });

export const EditQuizFormSchema = () =>
  Yup.object().shape({
    ...quizBaseFormFields,
    topic: Yup.string().required("The topic field is required"),
  });

export const QuizQuestionFormSchema = () =>
  Yup.object().shape({
    id: Yup.string().notRequired(),
    quiz_id: Yup.string().notRequired(),
    title: Yup.mixed()
      .test(
        "not empty",
        "The title field is required",
        (value) => !!Object.values(value).length
      )
      .required(),
    description: Yup.object().notRequired().nullable(),
    image: Yup.object().notRequired().nullable(),
    points: Yup.number().notRequired(),
    answers: Yup.array().notRequired(),
  });

export const QuizQuestionAnswerFormSchema = () =>
  Yup.object().shape({
    id: Yup.string().notRequired(),
    question_id: Yup.string().notRequired(),
    title: Yup.mixed()
      .test(
        "not empty",
        "The title field is required",
        (value) => !!Object.values(value).length
      )
      .required(),
    correct: Yup.boolean().notRequired(),
  });

export const DomainsSchema = () =>
  Yup.object().shape({
    domains: Yup.array().of(
      Yup.object().shape({
        domain: Yup.string()
          .matches(
            /^(?:(?:https?|ftp):\/\/)?(?:www\.)?(localhost|[a-z0-9-]+(?:\.[a-z0-9-]+){1,})$/i,
            "Invalid domain"
          )
          .required("Domain is required"),
      })
    ),
  });

export const ServicesFormSchema = () =>
  Yup.object().shape({
    visibilities: Yup.array()
      .of(
        Yup.object().shape({
          alias: Yup.string().required("Alias is required"),
          visible: Yup.boolean(),
          options: Yup.array().of(Yup.string()).notRequired(),
        })
      )
      .required("Visibilities is required"),
  });

export const ProviderBlacklistFormSchema = () =>
  Yup.object().shape({
    countries: Yup.array().of(Yup.string()),
  });

export const ProviderCurrenciesFormSchema = () =>
  Yup.object()
    .shape({
      currencies_supported: Yup.array().of(Yup.string()).notRequired(),
      currencies_unsupported: Yup.array().of(Yup.string()).notRequired(),
    })
    .test(
      "no-duplicate-currencies",
      "The same currency cannot be present in both fields.",
      function (values) {
        const { currencies_supported = [], currencies_unsupported = [] } =
          values || {};

        // Find intersection between the two arrays
        const intersection = currencies_supported.filter((currency) =>
          currencies_unsupported.includes(currency)
        );

        if (intersection.length > 0) {
          return this.createError({
            message: "The same currency cannot be present in both fields.",
            path: "currencies_supported", // You can also use "currencies_unsupported"
          });
        }

        return true;
      }
    );

export const AppVersionFormSchema = (isCreationForm = false) =>
  Yup.object().shape({
    active: Yup.boolean(),
    name: Yup.string().required("Name is required"),
    version: Yup.string()
      .required("Version is required")
      .test("valid-format", "Version format: 1, 1.0, 1.0.1", (value: string) =>
        /^\d+(?:\.\d+){0,2}$/g.test(value)
      ),
    type: Yup.string().required("Type is required").nullable(),
    priority: Yup.string().required("Priority is required").nullable(),
    comment: Yup.string().notRequired(),
    link: Yup.string().notRequired(),
    url_link: isValidUrl().notRequired(),
    file: Yup.string()
      .test(
        "required",
        `${isCreationForm ? "File or store" : "Store"} link is required`,
        (_value, ctx) => ctx?.parent?.link || ctx?.parent?.url_link
      )
      .test(
        "only-one-link",
        "Please provide either store link or build file",
        (_value, ctx) => !(ctx?.parent?.link && ctx?.parent?.url_link)
      ),
    title: Yup.object().notRequired().nullable(),
    text: Yup.object().notRequired().nullable(),
    key: Yup.string()
      .notRequired()
      .max(32, "Key must be at most 32 characters")
      .nullable(),
    iv: Yup.string()
      .notRequired()
      .max(16, "IV must be at most 16 characters")
      .nullable(),
  });

export const TextFieldByLocaleSchema = (fieldName, limit, required = false) =>
  Yup.lazy((obj) => {
    if (required && Object.keys(obj).length === 0) {
      return Yup.object().test(
        "required-empty",
        `${fieldName} is required`,
        () => false
      );
    }

    return Array.isArray(obj)
      ? Yup.array().nullable()
      : Yup.object()
          .shape(
            Object.keys(obj).reduce(
              (acc, lang) => ({
                ...acc,
                [lang]: Yup.string()
                  .max(
                    limit,
                    `${fieldName} must be at most ${limit} characters`
                  )
                  .test("required", `${fieldName} is required`, (value, ctx) =>
                    required
                      ? Object.keys(ctx.parent)?.some(
                          (key) => !!ctx.parent[key]
                        ) || !!value?.length
                      : true
                  )
                  .nullable(),
              }),
              {}
            )
          )
          .nullable();
  });

export const NotificationCategoryFormSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(60, "Name must be at most 60 characters")
      .required("Name is required"),
    title: TextFieldByLocaleSchema("Title", 60, true),
  });

export const NotificationCampaignSettingsFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean(),
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(60, "Name must be at most 60 characters")
      .required("Name is required"),
    type: Yup.string()
      .oneOf(["general", "by_list", "event_based"])
      .nullable()
      .required("Type is required"),
    event: Yup.string()
      .nullable()
      .typeError("Event is required while type event based")
      .test("Event is required while type event based", (value, ctx) =>
        ctx?.parent?.type === "event_based" ? !!value?.length : true
      ),
    category_id: Yup.number()
      .typeError("Category is required")
      .required("Category is required"),
    important: Yup.boolean(),
    allow_popup: Yup.boolean(),
    push_enabled: Yup.boolean(),
    title: TextFieldByLocaleSchema("Title", 60),
    description: TextFieldByLocaleSchema("Description", 1000),
    short_description: TextFieldByLocaleSchema("Short description", 250),
    button_text: TextFieldByLocaleSchema("Button text", 60),
    button_color: Yup.object().nullable(),
    image: Yup.object().nullable(),
    button_url: Yup.string().nullable(),
  });

const NotificationCampaignFiltersSchema = () =>
  Yup.object().shape({
    party_id: Yup.mixed()
      .notRequired()
      .nullable()
      .test("maxLength", "Party IDs must be at most 3000 characters", (val) =>
        typeof val === "string" ? !!val && val.length <= 3000 : true
      )
      .test("invalid", "Party IDs has invalid values", (val) =>
        !!val && typeof val === "string"
          ? val
              ?.split(/,(?=\s)?/)
              .every((el) => parseInt(el) && Number.isInteger(+el))
          : true
      ),
    label: Yup.array().of(Yup.string()).notRequired().nullable(),
  });

export const NotificationCampaignReceiversFormSchema = (
  validationParams: {
    fileSizeLimit?: number;
    types?: string | string[];
  } = {},
  fileUploader: boolean = false
) =>
  Yup.object().shape({
    user_filters: NotificationCampaignFiltersSchema().concat(
      Yup.object().shape({
        country: Yup.array().of(Yup.string()).notRequired().nullable(),
        vip: Yup.boolean(),
      })
    ),
    user_filters_file: Yup.mixed()
      .nullable()
      .test("fileExists", "File is not selected", (value) => {
        if (fileUploader) {
          return !!value;
        }
        return true;
      })
      .test("fileSize", "File size is too large", (value) => {
        const file = value as File | undefined;

        return file && validationParams.fileSizeLimit
          ? file.size <= validationParams.fileSizeLimit
          : true;
      })
      .test("type", "Unsupported file type", (value) => {
        const file = value as File | undefined;

        return file && validationParams.types && validationParams.types.length
          ? validationParams.types.includes(file.type)
          : true;
      }),
  });

const NotificationCampaignLifetimeSchema = () =>
  Yup.number()
    .typeError("Lifetime must be a number")
    .integer("Lifetime must be a whole number of days")
    .min(1, "Lifetime must be at least 1 day")
    .max(
      NOTIFICATION_CAMPAIGN_MAX_LIFETIME_DAYS,
      `Lifetime must be at most ${NOTIFICATION_CAMPAIGN_MAX_LIFETIME_DAYS} days`
    )
    .nullable()
    .notRequired();

export const NotificationCampaignScheduleFormSchema = (
  type: string,
  initialDates?: {
    start_sending_at: string | null;
    stop_sending_at: string | null;
  }
) =>
  Yup.object().shape({
    lifetime: NotificationCampaignLifetimeSchema(),
    start_sending_at: Yup.string()
      .required("is required")
      .test(
        "startDate",
        "must be greater than current date and time",
        (value, ctx) =>
          (value === initialDates?.start_sending_at &&
            ctx.parent.stop_sending_at === initialDates?.stop_sending_at) ||
          +new Date() < +dateUTCParser(value)
      )
      .nullable(),
    stop_sending_at:
      type === "event_based"
        ? Yup.string().required("End date is required").nullable()
        : Yup.string().nullable(),
  });

export const NotificationCampaignTestSendOutFormSchema = () =>
  Yup.object().shape({
    test_user_filters: NotificationCampaignFiltersSchema(),
  });

export const CreateNotificationCampaignFormSchema = () =>
  NotificationCampaignSettingsFormSchema()
    .concat(NotificationCampaignReceiversFormSchema())
    .concat(
      Yup.object().shape({
        start_sending_at: Yup.string()
          .required("is required")
          .test(
            "startDate",
            "must be greater than current date and time",
            (value) => +new Date() < +dateUTCParser(value)
          )
          .nullable(),
        lifetime: NotificationCampaignLifetimeSchema(),
        stop_sending_at: Yup.string()
          .nullable()
          .test("End date is required while type event based", (value, ctx) =>
            ctx?.parent?.type === "event_based" ? !!value?.length : true
          ),
      })
    );

export const GamificationFormSchema = ({ countries, languages }) =>
  Yup.object().shape({
    active: Yup.boolean().notRequired().nullable(),
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(60, "Name must be at most 60 characters")
      .required("Name is required"),
    slug: isValidSlug().required("Slug is required"),
    type: Yup.string().required("Type is required").nullable(),
    title: TextFieldByLocaleSchema("Title", 120),
    text: Yup.object().notRequired().nullable(),
    display_position: Yup.object().nullable(),
    display_condition: isValidDisplayCondition({
      countries,
      languages,
    }).nullable(),
  });

export const GamificationScheduleFormSchema = () =>
  Yup.object().shape({
    show_from: Yup.string().required("Start date is required"),
    show_to: Yup.string().required("End date is required"),
  });

export const GamificationConditionsFormSchema = () =>
  Yup.object().shape({
    balance_type: Yup.string()
      .required("Balance type is required")
      .typeError("Balance type is required"),
    conversions: Yup.lazy((obj) =>
      Yup.object().shape(
        Object.keys(obj).reduce(
          (acc, lang) => ({
            ...acc,
            [lang]: Yup.object()
              .shape({
                bet: Yup.object().shape({
                  from: Yup.string().required("Amount is required"),
                  to: Yup.string().required("Amount is required"),
                }),
                deposit: Yup.object().shape({
                  from: Yup.string().required("Amount is required"),
                  to: Yup.string().required("Amount is required"),
                }),
              })
              .nullable(),
          }),
          {}
        )
      )
    ),
  });

export const PointGameFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean().notRequired().nullable(),
    name: Yup.string()
      .min(2, "Point game name must be at least 2 characters")
      .max(60, "Point game name must be at most 60 characters")
      .required("Point game name is required"),
    slug: isValidSlug().required("Slug is required"),
    title: TextFieldByLocaleSchema("Title", 120),
    text: Yup.object().notRequired().nullable(),
    title_image: Yup.object().notRequired().nullable(),
    preview_image: Yup.object().notRequired().nullable(),
  });

export const PointGameLevelFormSchema = () =>
  Yup.object().shape({
    active: Yup.boolean().notRequired().nullable(),
    name: Yup.string()
      .min(2, "Level name must be at least 2 characters")
      .max(60, "Level game name must be at most 60 characters")
      .required("Level name is required"),
    score: Yup.number()
      .typeError("Score is required")
      .integer("Please enter a valid number.")
      .required("Score is required"),
    title: TextFieldByLocaleSchema("Title", 120),
    list_image: Yup.object().notRequired().nullable(),
    video: Yup.object().notRequired().nullable(),
    thumbnail_image: Yup.object().notRequired().nullable(),
  });

export const PlayerScoreFormSchema = () =>
  Yup.object().shape({
    scoreArr: Yup.array().of(
      Yup.object().shape({
        score: Yup.number()
          .typeError("Score is required")
          .integer("Please enter a valid number.")
          .required("Score is required"),
      })
    ),
  });

export const ReferralProgramSettingFormSchema = () =>
  Yup.object().shape({
    id: Yup.number().notRequired().nullable(),
    active: Yup.boolean(),
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(60, "Name must be at most 60 characters")
      .required("Name is required"),
    referrer_bonus_code: isValidBonusCode(
      "referrer_plan_id",
      "referrer_amount"
    ),
    referrer_plan_id: isRequiredWithOther("Amount", "referrer_amount"),
    referrer_amount: isRequiredWithOther("Bonus plan ID", "referrer_plan_id"),
    referrer_expired_interval: Yup.string().test(
      "required",
      "Expired interval is required",
      (value) => {
        const { claimable_number } = parseExpirationIntreval(value);
        return !!claimable_number && isValidNumber(claimable_number);
      }
    ),
    invitee_bonus_code: isValidBonusCode("invitee_plan_id", "invitee_amount"),
    invitee_plan_id: isRequiredWithOther("Amount", "invitee_amount"),
    invitee_amount: isRequiredWithOther("Bonus plan ID", "invitee_plan_id"),
    invitee_expired_interval: Yup.string().test(
      "required",
      "Expired interval is required",
      (value) => {
        const { claimable_number } = parseExpirationIntreval(value);
        return !!claimable_number && isValidNumber(claimable_number);
      }
    ),
    invitee_psp_methods: Yup.array()
      .of(Yup.mixed())
      .test(
        "required",
        "PSP methods field is required",
        (value) => !!value.length
      ),
    invitee_amount_from: Yup.object().notRequired().nullable(),
    invitee_amount_to: Yup.object().notRequired().nullable(),
    display_condition: Yup.object().notRequired().nullable(),
    title: Yup.mixed().optional().nullable(),
    description: Yup.mixed().optional().nullable(),
  });

export const BonusCancellationFormSchema = () =>
  Yup.object().shape({
    cancelled_comment: Yup.string()
      .required("Comment is required")
      .max(500, "Comment must be at most 500 characters"),
  });

export const DisplayPositionsFormSchema = (displayPositionsList) =>
  Yup.object().shape({
    type: Yup.string()
      .required("Type is required")
      .max(255, "Type must be at most 500 characters")
      .nullable(),
    value: Yup.string()
      .required("Display position is required")
      .max(255, "Value must be at most 500 characters")
      .test("fields-check", "", (value, context) => {
        const { parent, path } = context;

        if (
          displayPositionsList.some(
            (el) =>
              el.value?.toLowerCase() === value && el.type === parent?.type
          )
        ) {
          const errorForType = context.createError({
            path: "type",
            message: "Type already exists.",
          });

          const errorForValue = context.createError({
            path,
            message: "Display position already exists.",
          });

          return new Yup.ValidationError([errorForValue, errorForType]);
        }

        return true;
      }),
    comment: Yup.string()
      .max(500, "Comment must be at most 500 characters")
      .notRequired(),
  });
